import React, { useCallback, useMemo } from 'react';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import {
  GetClinicChannelStatusesQuery,
  UpdateUserBoardFilterSettingsMutationFn,
  useGetClinicChannelStatusesLazyQuery,
} from 'shared/types/graphql';
import { Box, Square } from '@televet/kibble-ui/build/chakra';
import { useAppSelector } from 'state/hooks';

export type ChannelStatusOption = NonNullable<GetClinicChannelStatusesQuery['findManyChannelStatus']>[0];

interface ConversationStatusFilterMenuProps {
  updateBoardSettings: UpdateUserBoardFilterSettingsMutationFn;
}

const ConversationStatusFilterMenu = ({ updateBoardSettings }: ConversationStatusFilterMenuProps): JSX.Element => {
  const { clinicUserId, currentClinicId, boardSettings } = useAppSelector((state) => state.board);

  const [getChannelStatuses, { data: channelStatusesData, loading: isLoadingChannelStatuses }] =
    useGetClinicChannelStatusesLazyQuery({
      variables: { clinicId: currentClinicId || '' },
    });

  const toggleChannelStatusFilter = useCallback(
    async (value?: (MenuItemProps | undefined)[]) => {
      if (!currentClinicId) return;

      const selectedIds = value?.map((item?: MenuItemProps) => {
        if (item && typeof item.value === 'string') return item.value;
        return;
      });

      await updateBoardSettings({
        variables: {
          where: {
            userId: clinicUserId || '',
            clinicId: currentClinicId || '',
          },
          data: {
            selectedChannelStatusIdsMap: {
              [currentClinicId as string]: selectedIds,
            },
          },
        },
      });
    },
    [clinicUserId, currentClinicId, updateBoardSettings],
  );

  const conversationStatusOptions = useMemo(
    () =>
      channelStatusesData?.findManyChannelStatus?.map(
        (status: ChannelStatusOption): MenuItemProps => ({
          label: status.name,
          value: status.id,
          isSelected:
            currentClinicId && boardSettings?.selectedChannelStatusIdsMap
              ? boardSettings?.selectedChannelStatusIdsMap[currentClinicId]?.includes(status.id)
              : false,
          groupLabel: status.channelStatusAction,
          leftElement: <Square size={3} borderRadius="sm" bg={status.color} />,
        }),
      ),
    [channelStatusesData?.findManyChannelStatus, currentClinicId, boardSettings?.selectedChannelStatusIdsMap],
  );

  const isFiltering = useMemo(() => {
    if (!currentClinicId || !boardSettings?.selectedChannelStatusIdsMap) return false;
    return !!boardSettings?.selectedChannelStatusIdsMap[currentClinicId]?.length;
  }, [currentClinicId, boardSettings?.selectedChannelStatusIdsMap]);

  return (
    <Box className="Board__Header-StausFilter">
      <Menu
        containerProps={{
          onClose: (value): Promise<void> => toggleChannelStatusFilter(value),
          onOpen: getChannelStatuses,
        }}
        buttonProps={{
          variant: isFiltering ? 'ghost' : 'secondarySubtle',
          ...(isFiltering && { bg: 'background.primaryAlpha.hover' }),
          size: 'xs',
          text: 'Filter Columns by Status',
          leftIconName: 'filter',
          showRightIcon: false,
        }}
        listProps={{
          isGrouped: true,
          isMultiSelect: true,
          isSearchable: true,
          menuItems: conversationStatusOptions,
          canSelectGroups: true,
          isLoading: isLoadingChannelStatuses,
        }}
      />
    </Box>
  );
};

export default ConversationStatusFilterMenu;
